/**
 * A balance walked forward, as a step line.
 *
 * Steps rather than a smooth line because that is what a balance does: it holds all
 * day and jumps when money moves. A diagonal between two points would draw money
 * leaking out between paydays, which never happens.
 *
 * Two readings matter and both are drawn, not left to the hover: where the line
 * crosses zero (the baseline, drawn only when the range reaches it) and the lowest
 * point, marked. The hover adds the exact figure for any day.
 */

import { useState } from 'react'
import type { ForecastPoint } from '../api.ts'
import { fmt, shortDate } from '../lib/format.ts'

function dayIndex(from: string, date: string): number {
  return Math.round(
    (Date.parse(`${date}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  )
}

function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

export function BalanceLine({
  points,
  from,
  through,
  low,
  height = 180,
  compact = false,
  label,
  hasFloor = true,
}: {
  points: ForecastPoint[]
  from: string
  through: string
  low: ForecastPoint
  height?: number
  /** A sparkline: no axis labels, no hover. */
  compact?: boolean
  label: string
  /** False for a card: negative is its nature, so below zero is not shaded as trouble. */
  hasFloor?: boolean
}) {
  const [hover, setHover] = useState<number | null>(null)
  const days = Math.max(1, dayIndex(from, through))
  const values = points.map((p) => p.balance)
  // Zero is always inside the range, so "is it above water" can be read off the shape.
  const top = Math.max(0, ...values)
  const bottom = Math.min(0, ...values)
  const span = Math.max(1, top - bottom)

  const x = (day: number) => (day / days) * 100
  const y = (balance: number) => 4 + (1 - (balance - bottom) / span) * 92

  // The balance on a given day: the last point on or before it.
  const at = (day: number) => {
    let value = points[0]?.balance ?? 0
    for (const p of points) {
      if (dayIndex(from, p.date) <= day) value = p.balance
      else break
    }
    return value
  }

  let path = ''
  points.forEach((p, i) => {
    const px = x(dayIndex(from, p.date))
    path += i === 0 ? `M${px},${y(p.balance)}` : `H${px}V${y(p.balance)}`
  })
  path += `H100`

  const hoverDate = hover === null ? null : addDays(from, hover)
  const hoverValue = hover === null ? null : at(hover)
  const lowDay = dayIndex(from, low.date)

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="block w-full"
          style={{ height }}
          role="img"
          aria-label={`${label}: lowest ${fmt(low.balance)} on ${shortDate(low.date)}`}
          onPointerMove={
            compact
              ? undefined
              : (event) => {
                  const rect = event.currentTarget.getBoundingClientRect()
                  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
                  setHover(Math.round(ratio * days))
                }
          }
          onPointerLeave={() => setHover(null)}
        >
          <title>{label}</title>
          {hasFloor && bottom < 0 && (
            <rect
              x="0"
              y={y(0)}
              width="100"
              height={Math.max(0, 96 - y(0))}
              fill="var(--color-blush-soft)"
              opacity="0.6"
            />
          )}
          <line
            x1="0"
            x2="100"
            y1={y(0)}
            y2={y(0)}
            stroke="var(--color-line-strong)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={path}
            fill="none"
            stroke="var(--color-sky-deep)"
            strokeWidth={compact ? 1.5 : 2}
            vectorEffect="non-scaling-stroke"
          />
          {hover !== null && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1="0"
              y2="100"
              stroke="var(--color-ink-dim)"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {/* The low point as HTML, so it stays round in a stretched viewBox. */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute rounded-full border-2 border-card ${
            hasFloor && low.balance < 0 ? 'bg-blush-deep' : 'bg-sky-deep'
          } ${compact ? 'h-2 w-2' : 'h-2.5 w-2.5'}`}
          style={{
            left: `${x(lowDay)}%`,
            top: `${(y(low.balance) / 100) * height}px`,
            transform: 'translate(-50%, -50%)',
          }}
        />
        {hoverDate && hoverValue !== null && (
          <div
            className="pointer-events-none absolute top-1 z-10 -translate-x-1/2 whitespace-nowrap rounded-lg bg-card px-2.5 py-1.5 text-[12px] shadow-[var(--shadow-tooltip)]"
            style={{ left: `${Math.min(88, Math.max(12, x(hover ?? 0)))}%` }}
          >
            <span className="text-muted">{shortDate(hoverDate)}</span>{' '}
            <span className={hoverValue < 0 ? 'text-blush-deep' : 'text-ink'}>
              {fmt(hoverValue)}
            </span>
          </div>
        )}
      </div>
      {!compact && (
        <div className="flex justify-between text-[11px] text-muted">
          <span>Today</span>
          <span>
            Lowest {fmt(low.balance)} on {shortDate(low.date)}
          </span>
          <span>{shortDate(through)}</span>
        </div>
      )}
    </div>
  )
}
